// 회원 정보 등 사용자 입력 텍스트 검증 유틸. client/src/util/inputValidation.ts 와 동일 정책.
// 정책:
//  - 양 끝 공백만 trim (중간 공백은 보존: "Hong Gildong" 유지)
//  - trim 후 빈 문자열은 필수 필드에서 거부
//  - 제어문자(U+0000~U+001F, U+007F, U+0080~U+009F)는 차단
//  - textarea처럼 줄바꿈이 자연스러운 필드는 multiline 옵션으로 LF(U+000A)/CR(U+000D)만 허용
//  - TAB(U+0009)은 단일행/다중행 모두 차단 (CSV 컬럼 깨짐 방지)

// 코드포인트 → \uXXXX escape 변환. 정규식 리터럴에 raw 제어문자를 박지 않기 위한 안전 장치.
const u = (cp: number): string => `\\u${cp.toString(16).padStart(4, '0').toUpperCase()}`;

// 단일행 필드 차단 범위: C0 제어(U+0000~U+001F) + DEL(U+007F) + C1 제어(U+0080~U+009F)
export const SINGLE_LINE_CONTROL_RE = new RegExp(
  `[${u(0x00)}-${u(0x1f)}${u(0x7f)}-${u(0x9f)}]`,
);

// 다중행 필드 차단 범위: 단일행과 동일하되 LF(U+000A)/CR(U+000D)만 허용
// TAB(U+0009)은 CSV 컬럼 깨짐 방지를 위해 다중행에서도 차단
export const MULTI_LINE_CONTROL_RE = new RegExp(
  `[${u(0x00)}-${u(0x09)}${u(0x0b)}-${u(0x0c)}${u(0x0e)}-${u(0x1f)}${u(0x7f)}-${u(0x9f)}]`,
);

export type TextValidationError = 'EMPTY' | 'TOO_LONG' | 'CONTROL_CHARS' | null;

export interface TextValidationOptions {
  required?: boolean;
  maxLength?: number;
  multiline?: boolean;
}

export const trimNormalize = (value: string | null | undefined): string => {
  if (value == null) return '';
  return value.trim();
};

export const hasControlChars = (value: string, multiline?: boolean): boolean => {
  const regex = multiline ? MULTI_LINE_CONTROL_RE : SINGLE_LINE_CONTROL_RE;
  return regex.test(value);
};

export const validateText = (
  value: string | null | undefined,
  opts: TextValidationOptions = {},
): TextValidationError => {
  const trimmed = trimNormalize(value);

  if (trimmed.length === 0) {
    return opts.required ? 'EMPTY' : null;
  }

  if (opts.maxLength != null && trimmed.length > opts.maxLength) {
    return 'TOO_LONG';
  }

  // 원본 value 기준으로 제어문자 검사. 양 끝 공백/개행이 trim에서 제거되더라도
  // 단일행 필드에 의도되지 않은 줄바꿈/탭이 섞여 들어오면 거부해야 한다.
  const valueToCheck = value ?? '';
  if (hasControlChars(valueToCheck, opts.multiline)) {
    return 'CONTROL_CHARS';
  }

  return null;
};
